import React, { useEffect, useState } from "react";
import { X, AlertTriangle, FileArchive, RotateCcw, Upload, ShieldAlert, Loader2 } from "lucide-react";
import {
  adminRestoreDryRun,
  adminRestoreExecute,
  adminRestoreExecuteUpload,
} from "@/lib/api";

/**
 * 4-step modal wizard for restoring a backup.
 *   1) Source — pick R2 backup OR upload a ZIP from disk.
 *   2) Selection — per-collection checkboxes + global strategy. Protected
 *      collections require an extra "I understand" toggle.
 *   3) Dry-run review — colour-coded diff table (insert / update / delete /
 *      unchanged) per chosen collection.
 *   4) Confirm — type "RESTORE" + 6-digit TOTP, then Execute.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onCompleted: (auditRow) => void   — fired after a successful execute
 *   - backups: array (the R2 list, passed in from the parent BackupTab)
 */
const STRATEGY_OPTIONS = [
  { value: "merge_upsert", label: "Merge / upsert by key (safe default)" },
  { value: "append_only", label: "Append only (never overwrite existing)" },
  { value: "replace", label: "Replace collection (DANGEROUS — wipes + reinserts)" },
];

const PRESETS = {
  apps_only: ["horse_apps"],
  public_content: [
    "horse_apps",
    "app_reviews",
    "blog_articles",
    "advert_submissions",
    "ads",
    "discount_codes",
    "i18n_cache",
    "newsletter_subscribers",
  ],
};

const RestoreWizard = ({ open, onClose, onCompleted, backups = [] }) => {
  const [step, setStep] = useState(1);
  const [sourceKind, setSourceKind] = useState("r2"); // 'r2' | 'upload'
  const [backupId, setBackupId] = useState("");
  const [file, setFile] = useState(null);
  const [globalStrategy, setGlobalStrategy] = useState("merge_upsert");
  const [diff, setDiff] = useState(null); // dry-run response
  const [selected, setSelected] = useState({}); // { collName: { selected, strategy, override } }
  const [confirmation, setConfirmation] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Reset on close (legit pattern — fires when modal toggles open/closed).
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSourceKind("r2");
      setBackupId(backups[0]?.id || "");
      setFile(null);
      setGlobalStrategy("merge_upsert");
      setDiff(null);
      setSelected({});
      setConfirmation("");
      setTotpCode("");
      setError("");
      setLoading(false);
      setExecuting(false);
    } else if (!backupId && backups.length) {
      setBackupId(backups[0].id);
    }
  }, [open, backups]);

  if (!open) return null;

  // -------------- Step 1 → 2: run dry-run --------------
  const runDryRun = async () => {
    setError("");
    setLoading(true);
    try {
      const args = sourceKind === "r2" ? { backupId } : { file };
      if (sourceKind === "r2" && !backupId) {
        throw new Error("Pick a backup to restore from");
      }
      if (sourceKind === "upload" && !file) {
        throw new Error("Choose a backup ZIP file to upload");
      }
      const d = await adminRestoreDryRun(args);
      setDiff(d);
      // Pre-select non-protected collections that have any change
      const sel = {};
      d.collections.forEach((c) => {
        const hasChange = c.insert + c.update > 0;
        sel[c.name] = {
          selected: !c.protected && hasChange,
          strategy: globalStrategy,
          override: false,
        };
      });
      setSelected(sel);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Dry-run failed");
    } finally {
      setLoading(false);
    }
  };

  // -------------- Step 2: presets / toggles --------------
  const applyPreset = (key) => {
    if (!diff) return;
    const allowed = key === "all" ? diff.collections.map((c) => c.name) : PRESETS[key] || [];
    const sel = { ...selected };
    diff.collections.forEach((c) => {
      sel[c.name] = {
        ...sel[c.name],
        selected: allowed.includes(c.name) && !c.protected,
      };
    });
    setSelected(sel);
  };

  const updateStrategy = (name, strategy) => {
    setSelected((s) => ({ ...s, [name]: { ...s[name], strategy } }));
  };
  const setGlobalStrategyAll = (strategy) => {
    setGlobalStrategy(strategy);
    const sel = { ...selected };
    Object.keys(sel).forEach((k) => {
      sel[k] = { ...sel[k], strategy };
    });
    setSelected(sel);
  };

  // -------------- Step 4: execute --------------
  const execute = async () => {
    setError("");
    setExecuting(true);
    try {
      const chosen = Object.entries(selected)
        .filter(([, v]) => v.selected)
        .map(([name, v]) => ({
          name,
          strategy: v.strategy,
          override_protection: !!v.override,
        }));
      if (chosen.length === 0) throw new Error("Select at least one collection");
      let result;
      if (sourceKind === "r2") {
        result = await adminRestoreExecute({
          backupId,
          collections: chosen,
          confirmation,
          totpCode,
        });
      } else {
        result = await adminRestoreExecuteUpload({
          file,
          collections: chosen,
          confirmation,
          totpCode,
        });
      }
      onCompleted?.(result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Restore failed");
    } finally {
      setExecuting(false);
    }
  };

  // -------------- Derived state --------------
  const selectedNames = Object.entries(selected).filter(([, v]) => v.selected).map(([k]) => k);
  const totalImpact = selectedNames.reduce((acc, name) => {
    const c = diff?.collections.find((x) => x.name === name);
    const strategy = selected[name]?.strategy;
    if (!c) return acc;
    if (strategy === "replace") acc.delete += c.in_current;
    acc.insert += c.insert;
    acc.update += strategy === "append_only" ? 0 : c.update;
    return acc;
  }, { insert: 0, update: 0, delete: 0 });

  const canExecute =
    confirmation === "RESTORE" && /^\d{6}$/.test(totpCode) && selectedNames.length > 0;

  // -------------- Render --------------
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="restore-wizard-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <RotateCcw size={18} />
            </div>
            <div>
              <h2 className="font-heading text-xl text-[#0F172A]">Restore from backup</h2>
              <div className="text-xs text-[#94A3B8]">Step {step} of 4</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="restore-wizard-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-auto flex-1">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="restore-wizard-error">
              {error}
            </div>
          )}

          {/* ===== STEP 1: SOURCE ===== */}
          {step === 1 && (
            <div className="space-y-4" data-testid="restore-wizard-step-1">
              <p className="text-sm text-[#64748B]">
                Pick a backup to restore from. <strong>Nothing happens yet</strong> — the next step
                is a dry-run review.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="restore-source-r2"
                  onClick={() => setSourceKind("r2")}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    sourceKind === "r2" ? "border-[#0F172A] bg-[#F8FAFC]" : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                  }`}
                >
                  <FileArchive size={18} className="mb-2 text-[#0F172A]" />
                  <div className="font-medium text-[#0F172A]">From R2 (recent backups)</div>
                  <div className="text-xs text-[#64748B] mt-1">Pick one of the last 10 stored backups.</div>
                </button>
                <button
                  type="button"
                  data-testid="restore-source-upload"
                  onClick={() => setSourceKind("upload")}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    sourceKind === "upload" ? "border-[#0F172A] bg-[#F8FAFC]" : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                  }`}
                >
                  <Upload size={18} className="mb-2 text-[#0F172A]" />
                  <div className="font-medium text-[#0F172A]">Upload a ZIP</div>
                  <div className="text-xs text-[#64748B] mt-1">Use a backup ZIP from your computer.</div>
                </button>
              </div>

              {sourceKind === "r2" && (
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-1 block">Choose backup</label>
                  <select
                    data-testid="restore-r2-select"
                    value={backupId}
                    onChange={(e) => setBackupId(e.target.value)}
                    className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm"
                  >
                    {backups.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.filename} · {(b.size_bytes / 1024).toFixed(1)} KB · {b.total_records} records
                        {b.source === "scheduled" ? "  (auto)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {sourceKind === "upload" && (
                <div>
                  <label className="text-sm font-medium text-[#0F172A] mb-1 block">Upload backup ZIP</label>
                  <input
                    type="file"
                    accept=".zip"
                    data-testid="restore-upload-input"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                  {file && (
                    <div className="text-xs text-[#64748B] mt-2">
                      {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 2: SELECTION ===== */}
          {step === 2 && diff && (
            <div className="space-y-4" data-testid="restore-wizard-step-2">
              <div className="text-sm text-[#64748B]">
                Select which collections to restore + the strategy. Protected collections require
                explicit &quot;I understand&quot; override.
              </div>

              {/* Presets + global strategy */}
              <div className="flex flex-wrap gap-2 items-center bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                <span className="text-xs font-medium text-[#0F172A] mr-2">Presets:</span>
                <button onClick={() => applyPreset("apps_only")} className="text-xs px-2 py-1 bg-white border border-[#E2E8F0] rounded-full hover:border-[#0F172A]" data-testid="restore-preset-apps">Apps only</button>
                <button onClick={() => applyPreset("public_content")} className="text-xs px-2 py-1 bg-white border border-[#E2E8F0] rounded-full hover:border-[#0F172A]" data-testid="restore-preset-public">All public content</button>
                <button onClick={() => applyPreset("all")} className="text-xs px-2 py-1 bg-red-50 border border-red-300 text-red-700 rounded-full hover:bg-red-100" data-testid="restore-preset-all">Everything (DANGEROUS)</button>
                <div className="flex-1" />
                <span className="text-xs font-medium text-[#0F172A]">Strategy:</span>
                <select
                  data-testid="restore-global-strategy"
                  value={globalStrategy}
                  onChange={(e) => setGlobalStrategyAll(e.target.value)}
                  className="text-xs border border-[#E2E8F0] rounded-lg px-2 py-1"
                >
                  {STRATEGY_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-xs uppercase text-[#94A3B8]">
                    <tr>
                      <th className="text-left px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Collection</th>
                      <th className="text-right px-3 py-2">Backup</th>
                      <th className="text-right px-3 py-2">Current</th>
                      <th className="text-right px-3 py-2 text-emerald-700">+Insert</th>
                      <th className="text-right px-3 py-2 text-blue-700">~Update</th>
                      <th className="text-right px-3 py-2 text-red-700">−Delete*</th>
                      <th className="text-left px-3 py-2">Strategy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.collections.map((c) => {
                      const sel = selected[c.name] || {};
                      return (
                        <tr key={c.name} className={`border-t border-[#E2E8F0] ${c.protected ? "bg-amber-50" : ""}`} data-testid={`restore-row-${c.name}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              data-testid={`restore-select-${c.name}`}
                              checked={!!sel.selected}
                              onChange={(e) => setSelected((s) => ({ ...s, [c.name]: { ...s[c.name], selected: e.target.checked } }))}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {c.name}
                            {c.protected && (
                              <span className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                <ShieldAlert size={9} /> Protected
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{c.in_backup}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-[#94A3B8]">{c.in_current}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-medium">{c.insert || ""}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-blue-700 font-medium">{c.update || ""}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-700 font-medium">{sel.strategy === "replace" ? c.in_current : ""}</td>
                          <td className="px-3 py-2">
                            <select
                              data-testid={`restore-strategy-${c.name}`}
                              value={sel.strategy || globalStrategy}
                              onChange={(e) => updateStrategy(c.name, e.target.value)}
                              disabled={!sel.selected}
                              className="text-xs border border-[#E2E8F0] rounded px-1.5 py-0.5 disabled:opacity-40"
                            >
                              {STRATEGY_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>{s.value}</option>
                              ))}
                            </select>
                            {c.protected && sel.selected && (
                              <label className="flex items-center gap-1 text-[10px] text-amber-700 mt-1">
                                <input
                                  type="checkbox"
                                  data-testid={`restore-override-${c.name}`}
                                  checked={!!sel.override}
                                  onChange={(e) => setSelected((s) => ({ ...s, [c.name]: { ...s[c.name], override: e.target.checked } }))}
                                />
                                I understand
                              </label>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-[#94A3B8]">* Delete count only applies when strategy is <code>replace</code>.</p>
            </div>
          )}

          {/* ===== STEP 3: REVIEW ===== */}
          {step === 3 && diff && (
            <div className="space-y-4" data-testid="restore-wizard-step-3">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                <div className="text-sm text-[#64748B] mb-3">You are about to restore <strong>{selectedNames.length}</strong> collection{selectedNames.length === 1 ? "" : "s"}:</div>
                <ul className="space-y-1 mb-4 max-h-48 overflow-auto">
                  {selectedNames.map((n) => (
                    <li key={n} className="text-xs font-mono text-[#0F172A] flex items-center justify-between">
                      <span>{n}</span>
                      <span className="text-[#94A3B8]">→ {selected[n].strategy}</span>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
                    <div className="text-2xl font-heading text-emerald-700">+{totalImpact.insert}</div>
                    <div className="text-xs text-[#94A3B8]">inserts</div>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
                    <div className="text-2xl font-heading text-blue-700">~{totalImpact.update}</div>
                    <div className="text-xs text-[#94A3B8]">updates</div>
                  </div>
                  <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
                    <div className="text-2xl font-heading text-red-700">−{totalImpact.delete}</div>
                    <div className="text-xs text-[#94A3B8]">deletes (replace)</div>
                  </div>
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-start gap-2">
                <ShieldAlert size={16} className="shrink-0 mt-0.5" />
                <span>A pre-restore backup will be created automatically as a rollback. You can always revert via the Backup tab.</span>
              </div>
            </div>
          )}

          {/* ===== STEP 4: CONFIRM ===== */}
          {step === 4 && (
            <div className="space-y-4" data-testid="restore-wizard-step-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <strong>This action is destructive.</strong> Type <code>RESTORE</code> + your fresh 2FA code to execute.
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-1 block">Type &quot;RESTORE&quot; to confirm</label>
                <input
                  type="text"
                  data-testid="restore-confirm-text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono"
                  placeholder="RESTORE"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-1 block">6-digit 2FA code</label>
                <input
                  type="text"
                  data-testid="restore-confirm-totp"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  maxLength={6}
                  className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 font-mono tracking-widest"
                  placeholder="123456"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer / nav */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-between items-center bg-[#F8FAFC]">
          <button
            type="button"
            onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
            disabled={loading || executing}
            className="text-sm text-[#64748B] hover:text-[#0F172A] disabled:opacity-40"
            data-testid="restore-wizard-back"
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step === 1 && (
            <button
              type="button"
              onClick={runDryRun}
              disabled={loading}
              data-testid="restore-wizard-next-dryrun"
              className="bg-[#0F172A] text-white text-sm font-medium rounded-full px-5 py-2 inline-flex items-center gap-2 hover:bg-[#1E293B] disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Analysing…" : "Run dry-run"}
            </button>
          )}
          {step === 2 && (
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={selectedNames.length === 0}
              data-testid="restore-wizard-next-review"
              className="bg-[#0F172A] text-white text-sm font-medium rounded-full px-5 py-2 inline-flex items-center gap-2 hover:bg-[#1E293B] disabled:opacity-40"
            >
              Continue · {selectedNames.length} selected →
            </button>
          )}
          {step === 3 && (
            <button
              type="button"
              onClick={() => setStep(4)}
              data-testid="restore-wizard-next-confirm"
              className="bg-amber-500 text-white text-sm font-medium rounded-full px-5 py-2 inline-flex items-center gap-2 hover:bg-amber-600"
            >
              I&apos;m ready to confirm →
            </button>
          )}
          {step === 4 && (
            <button
              type="button"
              onClick={execute}
              disabled={!canExecute || executing}
              data-testid="restore-wizard-execute"
              className="bg-red-600 text-white text-sm font-medium rounded-full px-5 py-2 inline-flex items-center gap-2 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {executing && <Loader2 size={14} className="animate-spin" />}
              {executing ? "Restoring…" : "EXECUTE RESTORE"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestoreWizard;
